import { ConfirmPopup } from '@neo/blocks/confirm-popup'
import { Button, ButtonWithLoader } from '@neo/index'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@neo/primitives/dialog'
import { RadioGroupComponent } from '@neo/primitives/radio-group'
import { useState } from 'react'
import type { RCard } from 'v2/types/api/billing'
import { useDeleteCardMutation, useUpdateDefaultCardMutation } from '../mutations'
import { PaymentCard } from './cards-list'

interface DeleteCardDialogProps {
  isOpen: boolean
  onClose: () => void
  card: RCard | null
  otherActiveCards: RCard[]
}

export const DeleteCardDialog = ({
  isOpen,
  onClose,
  card,
  otherActiveCards,
}: DeleteCardDialogProps) => {
  const hasOtherCards = otherActiveCards.length > 0
  const [selectedCardId, setSelectedCardId] = useState<number>(
    otherActiveCards[0]?.card_mapping_id ?? 0,
  )

  const { mutate: deleteCard, isPending: isDeleting } = useDeleteCardMutation()
  const { mutate: updateDefaultCard, isPending: isUpdatingDefault } = useUpdateDefaultCardMutation()

  const isPending = isDeleting || isUpdatingDefault

  const handleClose = () => {
    if (!isPending) {
      onClose()
    }
  }

  const handleDelete = () => {
    if (!card) return
    deleteCard({ card_mapping_id: card.card_mapping_id }, { onSuccess: handleClose })
  }

  const handleSwitchAndDelete = () => {
    if (!card || !selectedCardId) return
    updateDefaultCard(
      { card_mapping_id: selectedCardId },
      {
        onSuccess: () => {
          deleteCard({ card_mapping_id: card.card_mapping_id }, { onSuccess: handleClose })
        },
      },
    )
  }

  if (!card) return null

  // Single card (no alternatives) — use DestructiveConfirmPopup pattern
  if (!hasOtherCards) {
    return (
      <ConfirmPopup
        open={isOpen}
        setOpen={(open) => !open && handleClose()}
        title="Delete card"
        description="This is your only card and is used for auto-recharge. Deleting it will disable auto-recharge. Your account may run out of balance."
        okButtonProps={{
          variant: 'destructive',
          btnTxt: 'Delete anyway',
          onClick: handleDelete,
          'data-testid': 'button-delete-card-confirm',
        }}
        cancelButtonProps={{
          'data-testid': 'button-delete-card-cancel',
        }}
      />
    )
  }

  // Multiple cards — card selector dialog
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent data-testid="dialog-delete-card">
        <DialogHeader>
          <DialogTitle>Switch auto-recharge card</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <p className="text-p-400 text-secondary">
            This card is used for auto-recharge. Select another card to use for auto-recharge
            payments.
          </p>
          <RadioGroupComponent
            value={selectedCardId.toString()}
            onChange={(val) => setSelectedCardId(Number(val))}
            optionsClassName="flex flex-col gap-2"
            optionClassName="items-center"
            labelClassName="w-full"
            options={otherActiveCards.map((c) => ({
              label: (
                <div className="w-full">
                  <PaymentCard
                    showDelete={false}
                    size={28}
                    enableDefault={false}
                    card={c}
                    variant="recharge-modal"
                  />
                </div>
              ),
              value: String(c.card_mapping_id),
              disabled: isPending,
            }))}
            name="switch-card-list"
            label=""
          />
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isPending}
            data-testid="button-delete-card-cancel"
          >
            Cancel
          </Button>
          <ButtonWithLoader
            loading={isPending}
            onClick={handleSwitchAndDelete}
            data-testid="button-delete-card-confirm"
          >
            Switch & Delete
          </ButtonWithLoader>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
